import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomTable from "../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { EventForAuditTrail } from "../../../data-model/viewAllEvents";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import { fetchViewAllAuditTrail } from "../../../redux/slice/viewAllAuditTrailSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { generateFilteredDataForAllEvents } from "../helpers/filteredDataGenerator";
import { createFilterBasedOnEventType } from "../helpers/filterOptionsGenerator";
import {
  generateViewAllAuditTrailTableData,
  ViewAllAuditTrailColumnHeaders,
} from "../helpers/viewAllAuditTrailTableGenerator";

interface ViewAllEventsTableProps {
  viewAllAuditTrailTableRef: React.RefObject<HTMLDivElement>;
}

const ViewAllEventsTable = ({
  viewAllAuditTrailTableRef,
}: ViewAllEventsTableProps) => {
  const { orderId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { data, isLoading } = useSelector(
    (state: RootState) => state.viewAllAuditTrail,
  );
  const { filters } = useSelector((state: RootState) => state.filterValues);
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isTableRefActive, setIsTableRefActive] = useState(false);
  const [originalEvents, setOriginalEvents] = useState<EventForAuditTrail[]>(
    [],
  );
  const [filteredEvents, setFilteredEvents] = useState<EventForAuditTrail[]>(
    [],
  );
  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [initialQueryExecuted, setInitialQueryExecuted] =
    useState<boolean>(false);

  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.name === "view_all_events");
  }, [filters]);

  const isFilterApplied = useMemo(() => {
    return (
      validFilters &&
      validFilters.length > 0 &&
      validFilters?.[0]?.["filterCount"] > 0
    );
  }, [validFilters]);

  useEffect(() => {
    const filterData = [createFilterBasedOnEventType()];
    setFilterData(filterData);
  }, []);

  // Fetch data when component becomes active
  useEffect(() => {
    if (isTableRefActive && !initialQueryExecuted && orderId) {
      dispatch(fetchViewAllAuditTrail(orderId));
      setInitialQueryExecuted(true);
    }
  }, [isTableRefActive, initialQueryExecuted, orderId, dispatch]);

  // Apply filters and search when originalEvents or filters change
  useEffect(() => {
    if (originalEvents.length === 0) {
      return;
    }

    let filteredData = originalEvents;

    if (searchTerm !== "") {
      filteredData = filteredData.filter((event) =>
        event?.event_name?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (isFilterApplied) {
      filteredData = generateFilteredDataForAllEvents(
        filteredData,
        validFilters,
      );
    }

    setFilteredEvents(filteredData);
  }, [originalEvents, searchTerm, isFilterApplied, validFilters]);

  useEffect(() => {
    if (data) {
      const allOrders = data.entries?.patients?.[0]?.orders;

      const requiredOrder = allOrders?.find(
        (order) => order.order_id === orderId,
      );
      const events = requiredOrder?.events ?? [];
      setOriginalEvents(events);
      setFilteredEvents(events);
    }
  }, [data, orderId]);

  useEffect(() => {
    setTableData(generateViewAllAuditTrailTableData(filteredEvents));
  }, [filteredEvents]);

  useEffect(() => {
    if (viewAllAuditTrailTableRef.current) {
      setIsTableRefActive(true);
    }
  }, [viewAllAuditTrailTableRef]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  return (
    <div
      className="audit-trial--container h-full overflow-auto"
      ref={viewAllAuditTrailTableRef}
    >
      {isLoading ? (
        <>
          <div className="loader-message--container flex h-full items-center justify-center">
            <LoaderMessage message="Loading events..." />
          </div>
        </>
      ) : (
        <>
          <CustomTable
            tableHeaders={ViewAllAuditTrailColumnHeaders}
            tableData={tableData}
            tableName="view_all_events"
            pagesPerView={5}
            itemsPerPage={tableData.length}
            count={tableData.length}
            hidePagination={true}
            showRefreshButton={true}
            onRefreshButton={() => {
              dispatch(fetchViewAllAuditTrail(orderId ?? ""));
            }}
            placeholder="Search by Event Name"
            searchingText={(searchText: string) => {
              setSearchTerm(searchText);
            }}
            showFilterButton={true}
            filterSectionsData={filterData}
            onFilterApplyClicked={handleFilterApply}
            isFilterApplied={isFilterApplied}
            isDisabled={validFilters?.[0]?.["filterCount"] > 0}
          />
        </>
      )}
    </div>
  );
};

export default ViewAllEventsTable;
